// src/pages/SuppliersPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AddSupplierForm from "../components/AddSupplierForm";
import { API_BASE as RAW_API_BASE } from "../config";
import "./SuppliersPage.css";

/* ---------- Helpers ---------- */

function normalizeApiBase(base) {
  // evita casi tipo: "https://api.soscontrol.it/" o "https://api.../api"
  const b = String(base || "").trim().replace(/\/+$/, "");
  return b.replace(/\/api$/i, "");
}

const API_BASE = normalizeApiBase(RAW_API_BASE);

function normalizeRoles(user) {
  const roles = Array.isArray(user?.roles) ? user.roles.slice() : [];
  if (roles.length === 0 && user?.role) roles.push(user.role);
  return roles.map((r) => String(r).trim().toLowerCase()).filter(Boolean);
}

function hasAnyRole(user, allowed) {
  const roles = normalizeRoles(user);
  return allowed.some((r) => roles.includes(String(r).toLowerCase()));
}

async function readErrorMessage(res) {
  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    const data = await res.json().catch(() => null);
    if (!data) return `Errore HTTP ${res.status}`;
    return data.message || data.error || `Errore HTTP ${res.status}`;
  }
  const text = await res.text().catch(() => "");
  return text || `Errore HTTP ${res.status}`;
}

/**
 * ✅ Fetch wrapper: sempre /api/...
 * API_BASE in prod può essere "" => chiamate relative (/api/...)
 * API_BASE in dev es: "http://localhost:3001" => http://localhost:3001/api/...
 */
async function apiFetch(path, { token, headers, signal, ...options } = {}) {
  const p = String(path || "").startsWith("/") ? String(path) : `/${path}`;
  const url = `${API_BASE}${p}`;

  const res = await fetch(url, {
    ...options,
    signal,
    headers: {
      Accept: "application/json",
      ...(headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!res.ok) throw new Error(await readErrorMessage(res));

  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) return res.json().catch(() => null);
  return res.text().catch(() => "");
}

/* ---------- Page ---------- */

function SuppliersPage({ token, user }) {
  const canInteract = Boolean(token);

  const isDeveloper = useMemo(() => hasAnyRole(user, ["developer"]), [user]);

  const canManageSuppliers = useMemo(
    () => hasAnyRole(user, ["admin", "segretario", "tesoriere", "developer"]),
    [user]
  );

  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentSupplier, setCurrentSupplier] = useState(null);

  const abortRef = useRef(null);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setIsEditing(false);
    setCurrentSupplier(null);
  }, []);

  // ESC per chiudere modale
  useEffect(() => {
    if (!isModalOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeModal();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isModalOpen, closeModal]);

  const safeSuppliers = useMemo(() => (Array.isArray(suppliers) ? suppliers : []), [suppliers]);

  const filteredSuppliers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return safeSuppliers;

    return safeSuppliers.filter((s) => {
      const hay = [s?.name, s?.email, s?.phone, s?.tenantId]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [safeSuppliers, search]);

  const fetchSuppliers = useCallback(async () => {
    if (!token) return;

    // abort precedente
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError("");

    try {
      const data = await apiFetch("/api/suppliers", { token, signal: controller.signal });
      const arr = Array.isArray(data) ? data : [];
      arr.sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")));
      setSuppliers(arr);
    } catch (e) {
      if (e?.name === "AbortError") return;
      setError(e?.message || "Errore nel recupero fornitori");
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchSuppliers();
    return () => abortRef.current?.abort?.();
  }, [token, fetchSuppliers]);

  const openCreate = () => {
    if (!canManageSuppliers) return;
    setIsEditing(false);
    setCurrentSupplier(null);
    setIsModalOpen(true);
  };

  const openEdit = (supplier) => {
    if (!canManageSuppliers) return;
    setIsEditing(true);
    setCurrentSupplier(supplier || null);
    setIsModalOpen(true);
  };

  const handleSaveSupplier = useCallback(
    async (payload) => {
      if (!token || !canManageSuppliers) return;

      setError("");

      try {
        if (isEditing && currentSupplier?.id) {
          await apiFetch(`/api/suppliers/${currentSupplier.id}`, {
            token,
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
        } else {
          // in non-developer forziamo tenantId corretto
          const body = isDeveloper ? payload : { ...payload, tenantId: user?.tenantId };

          await apiFetch("/api/suppliers", {
            token,
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          });
        }

        closeModal();
        await fetchSuppliers();
      } catch (e) {
        setError(e?.message || "Errore salvataggio fornitore");
      }
    },
    [token, canManageSuppliers, isEditing, currentSupplier, fetchSuppliers, isDeveloper, user, closeModal]
  );

  const handleDeleteSupplier = useCallback(
    async (id) => {
      if (!token || !canManageSuppliers) return;

      if (!window.confirm("Vuoi eliminare questo fornitore?")) return;

      setError("");

      try {
        await apiFetch(`/api/suppliers/${id}`, { token, method: "DELETE" });
        await fetchSuppliers();
      } catch (e) {
        setError(e?.message || "Errore eliminazione fornitore");
      }
    },
    [token, canManageSuppliers, fetchSuppliers]
  );

  return (
    <div className="suppliersPage">
      <div className="spHero">
        <div className="spHero__inner">
          <div className="spHero__left">
            <div className="spKicker">Anagrafica</div>
            <h2 className="spTitle">Fornitori</h2>
            <div className="spSubtitle">
              {isDeveloper ? "Vista developer • tutti i tenant" : `Tenant: ${user?.tenantId ?? "—"}`}
            </div>
          </div>

          <div className="spHero__right">
            <div className="spSearch">
              <input
                className="spInput"
                type="search"
                placeholder="Cerca nome, email, telefono…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={!canInteract}
              />
            </div>

            <button className="spBtn spBtn--ghost" onClick={fetchSuppliers} disabled={!canInteract || loading} type="button">
              {loading ? "Aggiorno…" : "Ricarica"}
            </button>

            <button
              className="spBtn spBtn--primary"
              onClick={openCreate}
              disabled={!canInteract || !canManageSuppliers}
              type="button"
              title={canManageSuppliers ? "Aggiungi fornitore" : "Non hai i permessi per gestire i fornitori"}
            >
              + Aggiungi fornitore
            </button>
          </div>
        </div>
      </div>

      <div className="spContainer">
        {error && <div className="spAlert spAlert--error">{error}</div>}

        {!canManageSuppliers && canInteract && (
          <div className="spAlert spAlert--warning">Non hai i permessi per creare/modificare/eliminare fornitori.</div>
        )}

        <div className="spCard">
          <div className="spCard__head">
            <div className="spCard__meta">
              <span className="spPill">{filteredSuppliers.length}</span>
              <span className="spMetaText">fornitori</span>
            </div>
          </div>

          {loading ? (
            <div className="spEmpty">
              <div className="spEmpty__title">Caricamento…</div>
              <div className="spEmpty__text">Sto recuperando i fornitori salvati.</div>
            </div>
          ) : filteredSuppliers.length === 0 ? (
            <div className="spEmpty">
              <div className="spEmpty__title">Nessun fornitore</div>
              <div className="spEmpty__text">
                {canManageSuppliers ? "Clicca “Aggiungi fornitore” per inserirne uno." : "Non ci sono fornitori da mostrare."}
              </div>
            </div>
          ) : (
            <div className="spTableWrap" role="region" aria-label="Tabella fornitori">
              <table className="spTable">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>Telefono</th>
                    {isDeveloper && <th>Tenant</th>}
                    <th className="spThRight">Azioni</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredSuppliers.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <div className="spName">{s.name}</div>
                        <div className="spSub">
                          ID: <span className="spMono">{s.id}</span>
                        </div>
                      </td>

                      <td className="spMuted">{s.email || "—"}</td>
                      <td className="spMuted">{s.phone || "—"}</td>
                      {isDeveloper && <td className="spMuted">{s.tenantId ?? "—"}</td>}

                      <td className="spTdRight">
                        <div className="spRowActions">
                          <button className="spBtn spBtn--small" onClick={() => openEdit(s)} type="button" disabled={!canManageSuppliers}>
                            Modifica
                          </button>

                          <button
                            className="spBtn spBtn--small spBtn--danger"
                            onClick={() => handleDeleteSupplier(s.id)}
                            type="button"
                            disabled={!canManageSuppliers}
                          >
                            Elimina
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="spTableHint">Suggerimento: usa la ricerca per filtrare rapidamente.</div>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && canManageSuppliers && (
        <div
          className="spModalOverlay"
          onMouseDown={(e) => e.target === e.currentTarget && closeModal()}
          role="dialog"
          aria-modal="true"
          aria-label={isEditing ? "Modifica fornitore" : "Aggiungi fornitore"}
        >
          <div className="spModal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="spModalHeader">
              <div>
                <div className="spModalKicker">{isEditing ? "Modifica" : "Nuovo"}</div>
                <h3 className="spModalTitle">{isEditing ? "Modifica fornitore" : "Aggiungi fornitore"}</h3>
              </div>

              <button className="spIconBtn" onClick={closeModal} aria-label="Chiudi" type="button">
                ×
              </button>
            </div>

            <div className="spModalBody">
              <AddSupplierForm isEditing={isEditing} currentSupplier={currentSupplier} onSave={handleSaveSupplier} onCancel={closeModal} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuppliersPage;
